import React, { Component } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { CheckCircleOutlineRounded } from '@material-ui/icons';
import {
    fetchIntegrationProblems,
    downloadIntegrationProblemsCsv,
} from '../../../../../../jsx/actions/settingsAction';
import ProspectProblemsBadge, {
    PROBLEMS_COLOR,
    orderedProblemTypes,
    problemTypeLabel,
} from './ProspectProblemsBadge';

const INTEGRATION_TYPE = 'prospect';

// Un contrôle bloqué par « déjà en cours » désactive le bouton le temps que l'autre se termine.
const IN_PROGRESS_COOLDOWN_MS = 30 * 1000;
// Nombre de lignes rendues par type, étendu par paliers (un gros compte peut en compter des milliers).
const PAGE_SIZE = 100;

const SCOPE_LABELS = {
    all: 'Tout le compte',
    selection: 'Box du programme (liste blanche)',
};

// Message par code d'erreur du corps (409 not_enabled / in_progress, 503 load_failed, 504 timeout).
// Dans tous ces cas les compteurs précédents sont effacés : jamais de chiffres partiels.
const ERROR_MESSAGES = {
    not_enabled: "L'intégration Prospect n'est pas activée pour ce compte",
    in_progress: 'Un contrôle est déjà en cours pour ce compte, réessayez dans une minute',
    timeout: 'Le contrôle a pris trop de temps, réessayez plus tard',
    load_failed: 'Lecture des données impossible, aucun compteur affiché',
};

function messageFor(result) {
    // domainModel répond 404 « No Access » (pas 403) à un rôle sans droit sur la page Intégrations.
    if (result.status === 403 || (result.status === 404 && /no access/i.test(String(result.error || '')))) {
        return 'Votre rôle ne donne pas accès au contrôle';
    }
    if (result.code && ERROR_MESSAGES[result.code]) return ERROR_MESSAGES[result.code];
    // Repli si le corps d'erreur n'a pas de code (ancien backend, proxy intermédiaire).
    if (result.status === 504) return ERROR_MESSAGES.timeout;
    if (result.status === 503) return ERROR_MESSAGES.load_failed;
    return result.error || 'Contrôle impossible';
}

// Répertoire de la page courante (les pages d'administration sont des routes sœurs :
// .../configuration, .../userlist). Un lien s'ouvre dans un nouvel onglet : la page cible
// lit alors son paramètre au montage.
function siblingPageUrl(page, query) {
    const dir = window.location.pathname.replace(/[^/]*\/?$/, '');
    return dir + page + '?' + query;
}

// Aide « Comment corriger » par type. Le genre distingue fiche client et fiche agent.
const HOW_TO_FIX = {
    agent_zone: ['Ouvrir la fiche agent et choisir Région / Province.'],
    model_manufacturer: ['Correspondance à ajouter par MyJouleBox (fournisseur ou modèle inconnu).'],
    payment_plan: ["Type d'abonnement sans catégorie BGFA : décision métier puis correspondance par MyJouleBox."],
    phase: ['Renseigner Phase choisie au questionnaire compteur.'],
    kwh_missing: ["Attendu pour SparkMeter : la série Relevés couvre l'énergie."],
    position: [
        "Renseigner la position de l'installation (questionnaire ou carte) ; à défaut, celle relevée sur la fiche client à la vente est utilisée.",
        'Le vérificateur BGFA attend une position pour chaque installation.',
    ],
};
const HOW_TO_FIX_GENDER = {
    client: 'Client : ouvrir la fiche client et renseigner le genre.',
    agent: 'Agent : ouvrir la fiche agent et choisir le genre.',
};

// Types dont la correction relève de MyJouleBox (correspondance BGFA), pas d'une fiche.
const FIXED_BY_MYJOULEBOX = ['model_manufacturer', 'payment_plan', 'plan_minigrid', 'telemetry_missing'];

// Pastille de segment, lue dans item.segment fourni par domainModel (le frontal ne connaît pas
// la liste des fournisseurs). Absente d'un item sans segment (agent, ancien domainModel).
const SEGMENT_LABELS = { shs: 'SHS', minigrid: 'Mini-réseau' };
const SEGMENT_COLORS = { shs: '#00695c', minigrid: '#1565c0' };

function isAgentItem(item) {
    return !!(item.agentId || item.target === 'agent');
}

function howToFix(type, items) {
    if (type === 'gender') {
        const hasAgent = items.some(isAgentItem);
        const hasClient = items.some((it) => !isAgentItem(it));
        // Sans liste (compteur seul), les deux consignes.
        if (!hasAgent && !hasClient) return [HOW_TO_FIX_GENDER.client, HOW_TO_FIX_GENDER.agent];
        return [hasClient && HOW_TO_FIX_GENDER.client, hasAgent && HOW_TO_FIX_GENDER.agent].filter(Boolean);
    }
    return HOW_TO_FIX[type] || [];
}

function agentLink(item) {
    return siblingPageUrl('configuration', 'page=agent&agentId=' + encodeURIComponent(item.agentId || item.id));
}

function boxLink(item) {
    let query = 'boxAssoId=' + encodeURIComponent(item.boxAssoId);
    if (item.boxNumber) {
        query += '&boxNumber=' + encodeURIComponent(item.boxNumber);
    }
    return siblingPageUrl('userlist', query);
}

/**
 * Bloc « Contrôle des données BGFA » de l'onglet Intégration Prospect.
 * Lecture seule : « Contrôler les données » appelle GET .../problems (aucun envoi, aucune
 * entrée au journal) ; « Exporter CSV » télécharge la liste complète (audit côté serveur).
 * Aucun nom ni téléphone client : les clients sont identifiés par n° de box + ID client.
 * Masqué quand l'intégration n'est pas activée (le backend répond alors 409 not_enabled).
 */
class ProspectProblemsPanel extends Component {
    state = {
        scope: 'selection', // défaut : les box du programme (liste blanche), seules envoyées
        loading: false,
        exporting: false,
        report: null,
        error: '',
        // not_enabled : le bloc se masque et ne garde qu'une ligne d'information.
        notEnabled: false,
        cooldown: false,
        expanded: {},
        // Nombre de lignes affichées par type (palier de PAGE_SIZE).
        visible: {},
    };

    cooldownTimer = null;
    unmounted = false;
    rootRef = React.createRef();

    componentWillUnmount() {
        this.unmounted = true;
        if (this.cooldownTimer) {
            clearTimeout(this.cooldownTimer);
            this.cooldownTimer = null;
        }
    }

    handleScope = (e) => {
        // Changer le périmètre efface le rapport précédent (il ne correspond plus au choix).
        this.setState({ scope: e.target.value, report: null, error: '', expanded: {}, visible: {} });
    };

    componentDidUpdate(prevProps) {
        // L'intégration vient d'être (ré)activée ou désactivée : le refus « non activée » est périmé.
        if (prevProps.enabled !== this.props.enabled && this.state.notEnabled) {
            this.setState({ notEnabled: false, error: '', report: null, expanded: {}, visible: {} });
        }
    }

    // keepReport : un échec d'export ne doit pas effacer le rapport affiché (sauf not_enabled).
    applyFailure(result, keepReport) {
        const patch = { error: messageFor(result) };
        if (!keepReport || result.code === 'not_enabled') {
            patch.report = null;
            patch.expanded = {};
            patch.visible = {};
        }
        if (result.code === 'not_enabled') {
            patch.notEnabled = true;
        }
        if (result.code === 'in_progress') {
            patch.cooldown = true;
            if (this.cooldownTimer) clearTimeout(this.cooldownTimer);
            this.cooldownTimer = setTimeout(() => {
                this.cooldownTimer = null;
                if (!this.unmounted) this.setState({ cooldown: false });
            }, IN_PROGRESS_COOLDOWN_MS);
        }
        this.setState(patch);
    }

    handleCheck = () => this.runCheck(this.state.scope, false);

    // expandNonZero : déplie d'office les types dont le compteur est > 0.
    async runCheck(scope, expandNonZero) {
        this.setState({ scope, loading: true, error: '', report: null, expanded: {}, visible: {} });
        const result = await fetchIntegrationProblems(INTEGRATION_TYPE, scope);
        if (this.unmounted) return;
        this.setState({ loading: false });
        if (result.ok) {
            const patch = { report: result.data };
            if (expandNonZero) {
                const counts = (result.data && result.data.counts) || {};
                patch.expanded = {};
                Object.keys(counts).forEach((t) => {
                    if (counts[t] > 0) patch.expanded[t] = true;
                });
            }
            this.setState(patch);
        } else {
            this.applyFailure(result);
        }
    }

    /**
     * Appelée par le formulaire au clic sur une pastille « Données BGFA incomplètes » :
     * fait défiler jusqu'au bloc, lance le contrôle sur la liste blanche et déplie les types
     * en manque. Mêmes gardes que le bouton : rien n'est relancé pendant un contrôle, un
     * export ou le délai d'attente après « déjà en cours ».
     */
    checkAndExpand() {
        if (this.rootRef.current && this.rootRef.current.scrollIntoView) {
            this.rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        const { loading, exporting, cooldown, notEnabled } = this.state;
        if (!this.props.enabled || notEnabled || loading || exporting || cooldown) return;
        this.runCheck('selection', true);
    }

    handleExport = async () => {
        this.setState({ exporting: true, error: '' });
        const result = await downloadIntegrationProblemsCsv(INTEGRATION_TYPE, this.state.scope);
        if (this.unmounted) return;
        this.setState({ exporting: false });
        if (!result.ok) {
            this.applyFailure(result, true);
        }
    };

    toggleType = (type) => () => {
        this.setState((prev) => ({ expanded: { ...prev.expanded, [type]: !prev.expanded[type] } }));
    };

    showMore = (type) => () => {
        this.setState((prev) => ({ visible: { ...prev.visible, [type]: (prev.visible[type] || PAGE_SIZE) + PAGE_SIZE } }));
    };

    renderTarget(item) {
        if (isAgentItem(item)) {
            return item.agentName || item.agentId || item.id;
        }
        const box = `Box n° ${item.boxNumber || '—'} · Client ${item.clientId || '—'}`;
        // Valeur fautive (champs ajoutés par domainModel !124 ; absents d'un ancien backend).
        if (item.type === 'model_manufacturer' && (item.supplier || item.model)) {
            return `${box} · fournisseur : ${item.supplier || '—'} · modèle : ${item.model || '—'}`;
        }
        if (item.type === 'payment_plan') {
            if (item.noSubscription) return `${box} · aucun abonnement`;
            if (item.subscriptionType) return `${box} · abonnement : ${item.subscriptionType}`;
        }
        return box;
    }

    renderSegment(item) {
        const label = SEGMENT_LABELS[item.segment];
        if (!label) return null;
        return (
            <span
                data-testid="segment-badge"
                style={{
                    marginLeft: 8, padding: '1px 6px', borderRadius: 8, fontSize: 11, whiteSpace: 'nowrap',
                    color: '#fff', backgroundColor: SEGMENT_COLORS[item.segment],
                }}
            >
                {label}
            </span>
        );
    }

    renderLink(item) {
        if (FIXED_BY_MYJOULEBOX.indexOf(item.type) >= 0) {
            return <Typography variant="caption" color="textSecondary">À corriger par MyJouleBox</Typography>;
        }
        const isAgent = isAgentItem(item);
        if (isAgent) {
            if (item.editable === false) {
                return <Typography variant="caption" color="textSecondary">Agent géré par un autre compte</Typography>;
            }
            return <a href={agentLink(item)} target="_blank" rel="noopener noreferrer">Ouvrir la fiche agent</a>;
        }
        if (!item.boxAssoId) {
            return <Typography variant="caption" color="textSecondary">—</Typography>;
        }
        return <a href={boxLink(item)} target="_blank" rel="noopener noreferrer">Ouvrir la fiche box</a>;
    }

    renderItems(type, items) {
        const limit = this.state.visible[type] || PAGE_SIZE;
        const shown = items.slice(0, limit);
        return (
            <Box ml={3} mb={1}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Cible</TableCell>
                                <TableCell>Envoi</TableCell>
                                <TableCell>Fiche</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.map((item, index) => (
                                <TableRow key={`${type}-${item.id || ''}-${item.boxAssoId || ''}-${index}`}>
                                    <TableCell>{this.renderTarget(item)}{this.renderSegment(item)}</TableCell>
                                    <TableCell style={{ whiteSpace: 'nowrap' }}>{item.sent ? 'Envoyé' : 'Hors liste blanche'}</TableCell>
                                    <TableCell>{this.renderLink(item)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {items.length > shown.length && (
                    <Box mt={1}>
                        <Button size="small" variant="outlined" onClick={this.showMore(type)}>
                            Afficher plus ({items.length - shown.length} restantes)
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }

    renderReport(report) {
        const counts = report.counts || {};
        const items = Array.isArray(report.items) ? report.items : [];
        const discarded = report.discarded || {};
        // Types affichés : les 5 types connus (0 compris, pour voir qu'ils ont été contrôlés)
        // puis tout type inconnu du frontal renvoyé par le backend.
        const knownTypes = orderedProblemTypes(Object.assign({ agent_zone: 0, model_manufacturer: 0, gender: 0, payment_plan: 0, position: 0 }, counts));

        return (
            <Box mt={1}>
                <Box mb={1}>
                    <ProspectProblemsBadge problems={counts} />
                    {Object.keys(counts).every((t) => !counts[t]) && (
                        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                            <CheckCircleOutlineRounded style={{ color: '#2e7d32', fontSize: 20 }} />
                            <Typography variant="body2" style={{ color: '#2e7d32' }}>
                                Aucun manque détecté ({SCOPE_LABELS[report.scope] || report.scope}).
                            </Typography>
                        </Box>
                    )}
                </Box>

                {knownTypes.map((type) => {
                    const n = counts[type] || 0;
                    const typeItems = items.filter((it) => it.type === type);
                    const open = !!this.state.expanded[type];
                    return (
                        <Box key={type}>
                            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                <Typography variant="body2" style={{ fontWeight: 600, color: n > 0 ? PROBLEMS_COLOR : '#616161' }}>
                                    {problemTypeLabel(type)} : {n}
                                </Typography>
                                {n > 0 && (
                                    <Button size="small" onClick={this.toggleType(type)}>
                                        {open ? 'Masquer' : 'Afficher la liste'}
                                    </Button>
                                )}
                            </Box>
                            {n > 0 && howToFix(type, typeItems).length > 0 && (
                                <Box ml={3}>
                                    {howToFix(type, typeItems).map((line) => (
                                        <Typography key={line} variant="caption" color="textSecondary" component="div">
                                            Comment corriger : {line}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                            {open && this.renderItems(type, typeItems)}
                        </Box>
                    );
                })}

                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    Écartés : {discarded.installationsExcluded || 0} installation{(discarded.installationsExcluded || 0) > 1 ? 's' : ''} exclue
                    {(discarded.installationsExcluded || 0) > 1 ? 's' : ''}, {discarded.agentsUnreadable || 0} fiche{(discarded.agentsUnreadable || 0) > 1 ? 's' : ''} agent illisible
                    {(discarded.agentsUnreadable || 0) > 1 ? 's' : ''}.
                </Typography>
                {report.generatedAt && (
                    <Typography variant="caption" color="textSecondary" component="div">
                        Contrôle du {new Date(report.generatedAt).toLocaleString('fr-FR')}
                    </Typography>
                )}
            </Box>
        );
    }

    render() {
        const { scope, loading, exporting, report, error, notEnabled, cooldown } = this.state;

        // Intégration ni activée ni en simulation (ou 409 not_enabled du serveur) : le contrôle
        // est refusé, on explique quoi faire au lieu de ne rien afficher —
        // c'est aussi la cible du clic sur la pastille.
        if (!this.props.enabled || notEnabled) {
            return (
                <div ref={this.rootRef}>
                <Box p={1}>
                    <Divider style={{ marginBottom: 12 }} />
                    <Typography variant="subtitle2" gutterBottom>
                        Contrôle des données BGFA
                    </Typography>
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                        <InfoOutlinedIcon style={{ color: '#1565c0', fontSize: 20, marginTop: 1 }} />
                        <Typography variant="body2" style={{ color: '#0d47a1' }}>
                            Le détail des manques et les liens pour les corriger sont disponibles quand
                            l'intégration est activée ou en simulation (cocher l'une des deux cases puis
                            Enregistrer). En attendant, le CSV d'un envoi du journal indique les manques
                            de chaque enregistrement (colonne manques_bgfa).
                        </Typography>
                    </Box>
                </Box>
                </div>
            );
        }

        const busy = loading || exporting;

        return (
            <div ref={this.rootRef}>
            <Box p={1}>
                <Divider style={{ marginBottom: 12 }} />
                <Typography variant="subtitle2" gutterBottom>
                    Contrôle des données BGFA
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Lecture seule : rien n'est envoyé à Prospect et rien n'est ajouté au journal. Les manques ne
                    changent pas le statut des synchronisations. Les clients sont identifiés par leur numéro de
                    box et leur ID client (ni nom ni téléphone).
                </Typography>

                <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                    <select
                        aria-label="Périmètre du contrôle"
                        value={scope}
                        onChange={this.handleScope}
                        disabled={busy}
                        style={{ padding: '6px 8px', fontSize: 14 }}
                    >
                        <option value="selection">{SCOPE_LABELS.selection} (défaut)</option>
                        <option value="all">{SCOPE_LABELS.all}</option>
                    </select>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.handleCheck}
                        disabled={busy || cooldown}
                        startIcon={loading ? <CircularProgress size={16} /> : null}
                    >
                        {loading ? 'Contrôle en cours...' : 'Contrôler les données'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={this.handleExport}
                        disabled={busy || cooldown}
                        startIcon={exporting ? <CircularProgress size={16} /> : null}
                    >
                        {exporting ? 'Export en cours...' : 'Exporter CSV'}
                    </Button>
                </Box>

                {error && (
                    <Box mt={1}>
                        <Typography variant="body2" color="error">{error}</Typography>
                    </Box>
                )}

                {report && this.renderReport(report)}

                <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 8 }}>
                    Relevés compteurs manquants et kWh manquants : voir le dernier passage et le journal.
                </Typography>
            </Box>
            </div>
        );
    }
}

export default ProspectProblemsPanel;
